import { createProject } from '@api/settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
	Box,
	Button,
	Divider,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	Zoom
} from '@mui/material';
import { useToast } from 'hooks';
import { AuthContext } from 'hooks/useAuth';
import React, { FC, useMemo } from 'react';
import { IProjectData, ProjectContext } from 'routes/ProjectProvider';
import { IConsoleAuth } from 'types/login';
import { IProjectCreate } from 'types/settings';
import { ToastSeverity } from 'types/toast';
import { IProject } from 'types/users';
import ProjectCreateDialog from './ProjectCreateDialog';

interface IProjectHeaderProps {
	drawerOpen: boolean;
}

const ProjectHeader: FC<IProjectHeaderProps> = ({ drawerOpen }) => {
	const { addToast } = useToast();
	const { consoleUser } = React.useContext<IConsoleAuth>(AuthContext);
	const {
		currentProject,
		setCurrentProject,
		setCurrentProjectStorage,
		setCurrentCompany
	} = React.useContext<IProjectData>(ProjectContext);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const openMenu = Boolean(anchorEl);

	const [createNewProjectOpen, setCreateNewProjectOpen] = React.useState(false);

	const handleClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			setAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleClose = React.useCallback(() => {
		setCreateNewProjectOpen(false);
		setAnchorEl(null);
	}, []);

	const handleSelectProject = React.useCallback(
		async (project: IProject, newProject: boolean) => {
			setCurrentProjectStorage(project.id);
			if (!newProject) {
				setCurrentProject(project.id);
				setCurrentCompany(project.companyId);
			}
			window.location.reload();
		},
		[setCurrentProjectStorage, setCurrentProject, setCurrentCompany]
	);

	const handleCreateNewProject = React.useCallback(() => {
		setCreateNewProjectOpen(true);
	}, []);

	const handleCancelNewProject = React.useCallback(() => {
		setCreateNewProjectOpen(false);
	}, []);

	const handleSubmitNewProject = React.useCallback(
		async (values?: IProjectCreate) => {
			try {
				const project = await createProject(values!);
				handleSelectProject(project, true);
			} catch (ex) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('project.header.creation.toast'),
					error: ex
				});
			}
		},
		[addToast, handleSelectProject]
	);

	const companyToProjectMap = useMemo(() => {
		const m: Record<
			string,
			{ companyName: string; projects: IProject[]; maxAllowedProjects: number }
		> = {};
		consoleUser?.projects?.forEach((p) => {
			if (!m[p.companyId]) {
				m[p.companyId] = {
					companyName: p.companyName,
					projects: [],
					maxAllowedProjects: p.maxAllowedProjects
				};
			}
			m[p.companyId].companyName = p.companyName;
			m[p.companyId].projects.push(p);
		});
		return m;
	}, [consoleUser.projects]);

	const companyIds = useMemo(
		() => Object.keys(companyToProjectMap),
		[companyToProjectMap]
	);

	const currentCompanyName = useMemo(
		() =>
			consoleUser?.projects?.find((p) => p.id === currentProject)
				?.companyName || '',
		[consoleUser?.projects, currentProject]
	);

	const currentCompanyId = useMemo(
		() =>
			consoleUser?.projects?.find((p) => p.id === currentProject)?.companyId ||
			'',
		[consoleUser?.projects, currentProject]
	);

	const currentProjectName = useMemo(
		() =>
			consoleUser?.projects?.find((p) => p.id === currentProject)?.name || '',
		[consoleUser?.projects, currentProject]
	);

	const sortedData = useMemo(() => {
		const sortedCompanyIds = companyIds.sort((a, b) => {
			const aName = companyToProjectMap[a].companyName;
			const bName = companyToProjectMap[b].companyName;
			return aName.localeCompare(bName);
		});
		return sortedCompanyIds.map((id) => {
			const projects = companyToProjectMap[id].projects.sort((a, b) => {
				const aName = a.name;
				const bName = b.name;
				return aName.localeCompare(bName);
			});
			return {
				companyId: id,
				companyName: companyToProjectMap[id].companyName,
				projects
			};
		});
	}, [companyIds, companyToProjectMap]);

	const disableCreateProject =
		companyToProjectMap[currentCompanyId]?.maxAllowedProjects <=
		companyToProjectMap[currentCompanyId]?.projects.length;
	return (
		<Box
			pb={2}
			pt={2}
			mx={1}
			data-testid="project-header-container"
			display="flex"
			justifyContent="center"
		>
			<>
				<Tooltip
					title={intl.tr('project.header.selection.tooltip')}
					disableHoverListener={drawerOpen}
					arrow
					placement="right"
					TransitionComponent={Zoom}
					enterDelay={500}
				>
					<Button
						variant="outlined"
						aria-controls={openMenu ? 'basic-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={openMenu ? 'true' : undefined}
						onClick={handleClick}
						size="small"
						fullWidth
						sx={{
							borderColor: 'grey.400',
							minWidth: 40
						}}
					>
						{drawerOpen && (
							<Typography
								data-testid="project-button"
								color="text.secondary"
								variant="body1"
								textOverflow="ellipsis"
								overflow="hidden"
								display="inline-block"
								title={currentProjectName}
							>
								{currentProjectName}
							</Typography>
						)}
						<Box
							display="flex"
							flexDirection="row-reverse"
							flexGrow={1}
							marginRight="3px"
						>
							<KeyboardArrowDownIcon color="disabled" />
						</Box>
					</Button>
				</Tooltip>
				<Menu
					open={openMenu}
					data-testid="projects-menu"
					anchorEl={anchorEl}
					onClose={handleClose}
					MenuListProps={{
						'aria-labelledby': 'project-button'
					}}
					PaperProps={{
						style: {
							maxHeight: 400,
							maxWidth: 280,
							minWidth: 238
						}
					}}
				>
					{sortedData.map(({ companyId, companyName, projects }) => (
						<Box
							key={companyId}
							data-testid={`project-company-item-${companyId}`}
						>
							<Typography
								textOverflow="ellipsis"
								variant="body2"
								pl={1}
								marginBottom={1}
								mt={1}
								pr={1}
								noWrap
								title={companyName}
								data-testid="project-company-name"
							>
								{companyName}
							</Typography>
							{projects.map((project) => (
								<MenuItem
									key={project.id}
									data-testid={`project-item-${project.id}`}
									onClick={() => {
										handleSelectProject(project, false);
									}}
									selected={project.id === currentProject}
								>
									<Typography
										variant="inherit"
										color="text.secondary"
										noWrap
										title={project.name}
									>
										{project.name}
									</Typography>
								</MenuItem>
							))}
						</Box>
					))}
					<Divider />
					<Tooltip
						title={intl.tr(
							disableCreateProject
								? 'create.project.disabled'
								: 'create.project.enabled',
							{ companyName: currentCompanyName }
						)}
						arrow
						placement="right"
						TransitionComponent={Zoom}
						enterDelay={500}
					>
						<Box data-testid="create-new-project-box">
							<MenuItem
								data-testid="create-new-project"
								onClick={handleCreateNewProject}
								disabled={disableCreateProject}
							>
								<ListItemText
									primary={
										<Typography color="text.secondary" variant="body2">
											{intl.tr('projects.dropdown.create.new')}
										</Typography>
									}
									disableTypography
								/>
							</MenuItem>
						</Box>
					</Tooltip>
				</Menu>
				{createNewProjectOpen && (
					<ProjectCreateDialog
						onSubmit={handleSubmitNewProject}
						onCancel={handleCancelNewProject}
						companyName={currentCompanyName}
					/>
				)}
			</>
		</Box>
	);
};

export default ProjectHeader;
